// Claude: this entry must not statically import anything that touches Livewire,
// Alpine or window globals. After a deploy, Livewire's wire:navigate head-merge
// injects the new app.js into a still-running stale tab: in mergeNewHead
// (vendor/livewire/livewire/dist/livewire.esm.js) the `isScript(child)` injection
// is not an `else` of the tracked-asset check, so the script is injected even
// when a reload has already been scheduled via setTimeout — and navigate awaits
// that script fully loading, so it executes first.
//
// Static imports are evaluated before the importing module's body, so importing
// livewire.esm here would reassign window.Alpine and boot a second
// Livewire/Alpine before the guard below could possibly run. Deferring the real
// boot behind a dynamic import keeps the guard ahead of all framework
// initialisation, so a stale tab reloads instead of running two instances.
// Ticket Rec0BNAA3LJGZ (Sentry IMPERIUM-23XJ / IMPERIUM-23XH).
//
// Claude: the boot chunk now has a stable filename (vite.config.js), so a rolling
// deploy can no longer 404 it — any entry version pairs safely with any chunk
// version. Cache-busting moved to window.__empireBootUrl, a versioned specifier
// set by layouts/app.blade.php from AppServiceProvider::bootChunkUrl(); @vite-ignore
// stops Vite's import-analysis plugin from trying to statically resolve it. The
// retry/beacon/banner below stay for unrelated transient failures (flaky network,
// blocked request, corrupted cache) — Sentry lives inside the chunk that failed, so
// the beacon is the only telemetry path those still have.
const BOOT_RETRY_KEY = '__empireBootRetried';

// Claude: this delay was originally calibrated to outlast CloudFront's ~10s
// error-caching TTL (distribution E1F0INN4029YF7, v3.empire-medical.com, has no
// CustomErrorResponses and keys its default cache policy on cookies + the full query
// string) so a rolling-deploy 404 wouldn't reload straight back into the same cached
// 404. That specific case is now impossible (see the stable-filename comment above),
// but the delay is left as-is since the remaining failure modes (flaky network,
// blocked request, corrupted cache) can still race a still-deploying fleet, and
// shrinking it isn't required by this ticket's acceptance criteria.
const BOOT_RETRY_DELAY_MS = 12000;

function reportBootFailure(reason, terminal) {
    try {
        const payload = JSON.stringify({
            reason: String(reason?.message ?? reason ?? ''),
            page: window.location.href,
            chunk: window.__empireBootUrl ?? '',
            terminal,
        });

        navigator.sendBeacon('/hooks/client/boot-failure', new Blob([payload], { type: 'application/json' }));
    } catch {
        // Beacon unavailable — there is nothing else this page can do to report itself.
    }
}

function showBootNotice(message, background, withReloadButton) {
    const notice = document.createElement('div');

    notice.setAttribute('role', 'alert');
    notice.style.cssText = [
        'position:fixed',
        'inset:0 0 auto 0',
        'z-index:2147483647',
        'padding:12px 16px',
        `background:${background}`,
        'color:#fff',
        'font:14px/1.4 system-ui,sans-serif',
        'text-align:center',
    ].join(';');

    notice.append(message);

    if (withReloadButton) {
        const retry = document.createElement('button');

        retry.type = 'button';
        retry.textContent = 'Reload';
        retry.style.cssText =
            'margin-left:8px;padding:2px 10px;border:1px solid #fff;border-radius:4px;background:transparent;color:#fff;cursor:pointer;font:inherit';
        retry.addEventListener('click', () => window.location.reload());

        notice.append(retry);
    }

    document.body.append(notice);
}

if (window.__empireBooted) {
    window.location.reload();
} else {
    window.__empireBooted = true;

    import(/* @vite-ignore */ window.__empireBootUrl).then(
        () => {
            try {
                sessionStorage.removeItem(BOOT_RETRY_KEY);
            } catch {
                // Storage unavailable (private mode, blocked cookies) — nothing to clear.
            }
        },
        (error) => {
            let spent = false;

            try {
                spent = Boolean(sessionStorage.getItem(BOOT_RETRY_KEY));

                if (!spent) {
                    sessionStorage.setItem(BOOT_RETRY_KEY, '1');
                }
            } catch {
                // Without storage a reload could loop, so treat this as terminal.
                spent = true;
            }

            reportBootFailure(error, spent);

            if (spent) {
                showBootNotice('This page did not finish loading and will not work correctly. ', '#b91c1c', true);

                return;
            }

            // No Reload button here on purpose: reloading inside the error-cache window
            // would re-fail and spend the one retry, landing the user on the red notice.
            showBootNotice('This page is finishing an update and will reload in a moment.', '#1d4ed8', false);

            window.setTimeout(() => window.location.reload(), BOOT_RETRY_DELAY_MS);
        }
    );
}
